import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import apiService from "../services/apiService";
import { useOrganization } from "../context/OrganizationContext";
import { useProject } from "../context/ProjectContext";

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };
const fadeUp = { hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.35 } } };

/** Same tool catalog rendered on the public /docs page. Keep in sync. */
const MCP_TOOLS = [
  { cat: "Chains",   name: "list_chains",         desc: "List active chains with rps cap, archive support, and pricing." },
  { cat: "Chains",   name: "get_chain_status",    desc: "Latest health for a chain — block height, latency, sync state." },
  { cat: "Requests", name: "rpc_call",            desc: "Make a JSON-RPC call (any method) on a chain." },
  { cat: "Requests", name: "evm_get_balance",     desc: "Read native-token balance for an EVM address." },
  { cat: "Requests", name: "evm_get_block",       desc: "Fetch a block by number or hash." },
  { cat: "Requests", name: "evm_get_transaction", desc: "Fetch a transaction + receipt by hash." },
  { cat: "Requests", name: "cosmos_get_balance",  desc: "Read balances on a Cosmos chain (REST)." },
  { cat: "Requests", name: "cosmos_get_block",    desc: "Fetch a Cosmos block by height." },
  { cat: "Requests", name: "cosmos_get_staking",  desc: "Validator + delegation info on a Cosmos chain." },
  { cat: "Requests", name: "solana_get_balance",  desc: "Read SOL balance for a Solana address." },
  { cat: "Account",  name: "list_api_keys",       desc: "List your org's keys with status + per-key analytics." },
  { cat: "Account",  name: "get_subscription",    desc: "Current plan, RPS, monthly cap, daily/monthly used." },
  { cat: "Account",  name: "get_usage",           desc: "Live quota counters for the current org." },
];
const MCP_GROUPS = ["Chains", "Requests", "Account"];
const MCP_BY_GROUP = MCP_GROUPS.map((g) => ({
  title: g,
  items: MCP_TOOLS.filter((t) => t.cat === g),
}));

/* Inline SVGs — same idiom used on the rest of the dashboard. */
const I = {
  plug: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden>
      <path d="M9 2v4M15 2v4M5 8h14l-1 6a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4zM12 18v4" />
    </svg>
  ),
  copy: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden>
      <path d="M9 9h11v11H9zM5 5h11v4M5 5v11h4" />
    </svg>
  ),
  eye: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  eyeOff: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden>
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a19.86 19.86 0 0 1 5.06-5.94M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a19.5 19.5 0 0 1-3.17 4.19M14.12 14.12a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ),
};

/** Replace the live key value in the rendered JSON with a fixed-length
 *  bullet string so the field is visually hidden but still copy-pasteable
 *  if the user copies what's on screen. The Copy button always copies the
 *  REAL key regardless of visibility state. */
const maskKey = (k) => {
  if (!k || k === "sk_live_...") return k;
  return "•".repeat(Math.max(24, k.length));
};

const Card = ({ children, className = "" }) => (
  <div className={`bg-landing-section border border-white/[0.06] rounded-sm p-4 ${className}`}>{children}</div>
);

const buildMcpJson = (keyValue) => `{
  "mcpServers": {
    "datanodes": {
      "url": "https://mcp.datanodes.nodefleet.net/mcp",
      "headers": {
        "x-api-key": "${keyValue}"
      }
    }
  }
}`;

const MCPIntegration = () => {
  const { currentOrganization } = useOrganization();
  const { selectedProject } = useProject();
  const [keys, setKeys] = useState([]);
  const [selectedKeyId, setSelectedKeyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    document.title = "MCP integration — DataNodes";
  }, []);

  // Load keys on org / project change. Scope to project if set, fall back
  // to org-wide. The user picks one to embed in the JSON config.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!currentOrganization?.id) {
        setKeys([]);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const list = await apiService.getApiKeys(
          currentOrganization.id,
          selectedProject?.uuid || null,
        );
        if (cancelled) return;
        const items = Array.isArray(list)
          ? list
          : Array.isArray(list?.results)
            ? list.results
            : [];
        const active = items.filter((k) => k.is_active !== false);
        setKeys(active);
        if (active.length > 0) {
          setSelectedKeyId((cur) => {
            // Preserve current selection if still present, else first key
            const stillThere = active.find((k) => k.id === cur);
            return stillThere ? cur : active[0].id;
          });
        } else {
          setSelectedKeyId(null);
        }
      } catch {
        if (!cancelled) setKeys([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [currentOrganization?.id, selectedProject?.uuid]);

  const selectedKey = useMemo(
    () => keys.find((k) => k.id === selectedKeyId) || keys[0] || null,
    [keys, selectedKeyId],
  );
  const keyValue = selectedKey?.key || "sk_live_...";
  // Real JSON (always uses the actual key) is what we copy. Display JSON
  // masks the key behind bullets until the user explicitly clicks the eye.
  // Default-hidden because this view often gets shown over screen-shares /
  // pair-programming; same defensive treatment as the dashboard.
  const realJson = buildMcpJson(keyValue);
  const displayJson = buildMcpJson(showKey ? keyValue : maskKey(keyValue));

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(realJson);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard API not available */
    }
  };

  return (
    <div className="min-h-full bg-[#0a0a0a] text-white px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="max-w-[1280px] mx-auto">

        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2.5 mb-1">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-sm bg-primary/10 border border-primary/25 text-primary">
                <I.plug />
              </span>
              <h1 className="font-title text-2xl sm:text-3xl font-bold text-white">
                MCP integration
              </h1>
            </div>
            <p className="font-secondary text-sm text-gray-400">
              Connect any MCP-compatible AI tool to your DataNodes account. We're LLM-agnostic — same JSON works everywhere.
            </p>
          </div>
        </div>

        <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-5">

          {/* Config panel */}
          <motion.div variants={fadeUp}
            className="relative overflow-hidden bg-landing-section border border-white/[0.06] rounded-sm p-5 sm:p-6">
            <div
              className="pointer-events-none absolute inset-0 opacity-40"
              style={{
                background: "linear-gradient(135deg, rgba(73,183,248,0.10) 0%, rgba(0,0,0,0) 60%)",
              }}
              aria-hidden
            />
            <div className="relative z-10">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
                <div>
                  <h2 className="font-title text-base font-semibold text-white">Configuration (works for every MCP client)</h2>
                  <p className="font-secondary text-xs text-gray-400 mt-1">
                    Drop this block into your client's MCP config file. The
                    key below is one of <span className="text-white">your</span> keys — pick a different
                    one if you want.
                  </p>
                </div>
                {keys.length > 1 && (
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.18em] text-gray-500 mb-1">
                      Use API key
                    </label>
                    <select
                      value={selectedKeyId || ""}
                      onChange={(e) => setSelectedKeyId(e.target.value)}
                      className="font-secondary bg-[#0c0c0c] border border-white/10 rounded-sm px-3 py-1.5 text-xs text-white focus:border-primary/40 focus:outline-none"
                    >
                      {keys.map((k) => (
                        <option key={k.id} value={k.id}>
                          {k.name || k.key.slice(0, 18)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {loading ? (
                <div className="h-32 flex items-center justify-center text-xs text-gray-500">Loading your keys…</div>
              ) : keys.length === 0 ? (
                <div className="rounded-sm border border-white/10 bg-black/40 p-4 text-sm text-gray-400">
                  You don't have any active API keys yet. Create one from the Dashboard, then come back to copy the config with your key embedded.
                </div>
              ) : (
                <div className="relative">
                  <pre className="p-4 pr-[8.5rem] rounded-sm bg-black/60 border border-white/[0.06] text-[12.5px] leading-relaxed text-gray-300 font-mono overflow-x-auto whitespace-pre">{displayJson}</pre>
                  <div className="absolute top-3 right-3 inline-flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setShowKey((v) => !v)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-white/10 bg-black/50 text-[10px] uppercase tracking-wider text-gray-300 hover:text-white hover:border-primary/30 hover:bg-primary/10 transition-colors"
                      aria-label={showKey ? "Hide API key" : "Show API key"}
                      aria-pressed={showKey}
                    >
                      {showKey ? <I.eyeOff /> : <I.eye />}
                      {showKey ? "Hide" : "Show"}
                    </button>
                    <button
                      type="button"
                      onClick={onCopy}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-white/10 bg-black/50 text-[10px] uppercase tracking-wider text-gray-300 hover:text-white hover:border-primary/30 hover:bg-primary/10 transition-colors"
                      aria-label="Copy MCP config"
                    >
                      <I.copy />
                      {copied ? "Copied" : "Copy"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Where to put it */}
          <motion.div variants={fadeUp}>
            <h2 className="font-title text-sm font-semibold text-white mb-3 mt-2">Where to put the config</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <Card>
                <h3 className="text-[11px] uppercase tracking-wider text-primary mb-2">Claude Desktop</h3>
                <code className="text-[11px] text-gray-300 break-all">~/Library/Application Support/Claude/claude_desktop_config.json</code>
              </Card>
              <Card>
                <h3 className="text-[11px] uppercase tracking-wider text-[#a5a0e6] mb-2">Claude Code</h3>
                <p className="text-[11px] text-gray-400"><code>~/.claude.json</code> or per-project <code>.mcp.json</code></p>
              </Card>
              <Card>
                <h3 className="text-[11px] uppercase tracking-wider text-primary mb-2">Codex</h3>
                <code className="text-[11px] text-gray-300 break-all">~/.codex/config.json</code>
              </Card>
              <Card>
                <h3 className="text-[11px] uppercase tracking-wider text-[#a5a0e6] mb-2">Cursor</h3>
                <p className="text-[11px] text-gray-400">Settings → MCP → <span className="text-white">Add server</span> with the same JSON.</p>
              </Card>
              <Card>
                <h3 className="text-[11px] uppercase tracking-wider text-primary mb-2">Continue.dev</h3>
                <code className="text-[11px] text-gray-300 break-all">~/.continue/config.json</code>
              </Card>
              <Card>
                <h3 className="text-[11px] uppercase tracking-wider text-[#a5a0e6] mb-2">Anything else MCP-compatible</h3>
                <p className="text-[11px] text-gray-400">Same JSON shape. The <code>url</code> + <code>headers</code> spec is universal.</p>
              </Card>
            </div>
          </motion.div>

          {/* Available tools */}
          <motion.div variants={fadeUp}>
            <h2 className="font-title text-sm font-semibold text-white mb-3 mt-2">Available tools</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {MCP_BY_GROUP.map(({ title, items }, idx) => {
                const isMorado = idx % 2 === 1;
                return (
                  <Card key={title}>
                    <h3 className={`text-[11px] uppercase tracking-wider mb-3 ${isMorado ? "text-[#a5a0e6]" : "text-primary"}`}>
                      {title}
                    </h3>
                    <ul className="space-y-2.5 text-xs text-gray-400">
                      {items.map(({ name, desc }) => (
                        <li key={name} className="flex flex-col">
                          <code className={`font-mono text-[11px] ${isMorado ? "text-[#c5c0f0]" : "text-primary"}`}>
                            {name}
                          </code>
                          <span className="text-gray-400 mt-0.5">{desc}</span>
                        </li>
                      ))}
                    </ul>
                  </Card>
                );
              })}
            </div>
          </motion.div>

          {/* Footer note */}
          <motion.div variants={fadeUp}>
            <div className="flex items-start gap-2.5 p-3 rounded-sm bg-primary/[0.07] border border-primary/20">
              <p className="font-secondary text-xs text-primary/85 leading-relaxed">
                Quota is enforced just like direct API calls — same per-org bucket, same plan limits. MCP traffic counts toward your monthly cap. See <a href="/#/docs" className="underline underline-offset-2">the docs</a> for the full integration guide.
              </p>
            </div>
          </motion.div>

        </motion.div>
      </div>
    </div>
  );
};

export default MCPIntegration;
